import { Suspense } from "react";
import { Link, Outlet, useLocation } from "react-router";

import { PageLoader } from "@/components/PageLoader";
import { PROTECTED_ROUTES, ROUTES } from "@/constants/routes";
import { SellWizardProvider, useSellWizard } from "@/features/listings/context/SellWizardContext";
import { TipsSidebarCard } from "@/features/listings/components/TipsSidebarCard";
import { WizardStepper, type WizardStep } from "@/features/listings/components/WizardStepper";

const STEPS: WizardStep[] = [{ label: "Media" }, { label: "Details" }, { label: "Pricing & Location" }];

const STEP_BY_PATH: Record<string, number> = {
  [PROTECTED_ROUTES.sellMedia]: 1,
  [PROTECTED_ROUTES.sellDetails]: 2,
  [PROTECTED_ROUTES.sellFinalize]: 3,
};

function WizardChrome() {
  const location = useLocation();
  const { draft } = useSellWizard();
  const currentStep = STEP_BY_PATH[location.pathname] ?? 1;
  // docs/ui/layouts.md: the tips/live-preview sidebar renders on steps 1–2
  // only, desktop only (TipsSidebarCard itself hides on mobile).
  const showTips = currentStep < 3;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border px-4 py-4">
        <div className="mx-auto flex max-w-[960px] items-center justify-between gap-6">
          <Link to={ROUTES.home} className="shrink-0 text-h3 text-foreground">
            BazaarX
          </Link>
          <div className="w-full max-w-[520px]">
            <WizardStepper steps={STEPS} currentStep={currentStep} />
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8">
        <div className="mx-auto flex max-w-[960px] items-start gap-8">
          <div className="w-full max-w-[640px]">
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
          {showTips && <TipsSidebarCard draft={draft} />}
        </div>
      </main>
    </div>
  );
}

/**
 * docs/ui/layouts.md's `WizardLayout` — used by the 3 listing-creation
 * steps. No footer, no account sidebar/nav, no `BottomNav` (explicitly
 * suppressed, same rule as `GuestAuthLayout`): a wizard is a focused task,
 * global nav is a distraction from finishing it. The step-progress header
 * replaces standard nav entirely; each step page renders its own fixed
 * bottom action bar (`WizardActionBar`) rather than the layout owning it,
 * since Back/Continue vs. Publish/Save-Draft differ per step in a way a
 * shared slot would need per-page callbacks for anyway.
 */
export function WizardLayout() {
  return (
    <SellWizardProvider>
      <WizardChrome />
    </SellWizardProvider>
  );
}
